import {
    type DashboardAccessInput,
    type DashboardExportState,
    getDashboardAccess,
    getDashboardExportState,
    listAppGroups
} from '@/lib/dashboards-api';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import useSWR from 'swr';

import { DashboardAccessEditor } from '@/components/Artifacts/DashboardAccessEditor';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PillTabsList } from '@/components/ui/pill-tabs-list';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Tabs, TabsContent } from '@/components/ui/tabs';

import { AppGroupField } from './AppGroupField';

const TABS = [
    { value: 'details', label: 'Details' },
    { value: 'access', label: 'Access' }
];

interface ExportToBigDialogProps {
    open: boolean;
    dashboardId: string;
    title: string;
    onOpenChange: (open: boolean) => void;
    onExport: (
        name: string,
        grouping: string,
        access: DashboardAccessInput | undefined
    ) => Promise<void>;
}

export function ExportToBigDialog({
    open,
    dashboardId,
    title,
    onOpenChange,
    onExport
}: ExportToBigDialogProps) {
    const [tab, setTab] = useState('details');
    const [name, setName] = useState(title);
    const [grouping, setGrouping] = useState('');
    const [accessLoaded, setAccessLoaded] = useState(false);
    const [accessError, setAccessError] = useState<string | null>(null);
    const [userIds, setUserIds] = useState<Set<string>>(new Set());
    const [groupIds, setGroupIds] = useState<Set<string>>(new Set());
    const [organization, setOrganization] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [exportState, setExportState] = useState<DashboardExportState | null>(
        null
    );
    const [error, setError] = useState<string | null>(null);

    const { data: groups = [] } = useSWR(
        open ? 'dashboard-app-groups' : null,
        listAppGroups,
        { revalidateOnFocus: false, shouldRetryOnError: false }
    );

    useEffect(() => {
        if (!open) return;
        setName(title);
        setGrouping('');
        setTab('details');
        setError(null);
        setAccessLoaded(false);
        setAccessError(null);
        setExportState(null);
        getDashboardExportState(dashboardId)
            .then((state) => {
                setExportState(state);
                if (state.name) setName(state.name);
                if (state.grouping) setGrouping(state.grouping);
            })
            .catch(() => setExportState(null));
        getDashboardAccess(dashboardId)
            .then((access) => {
                setUserIds(new Set(access.users.map((user) => user.userId)));
                setGroupIds(
                    new Set(access.groups.map((group) => group.groupId))
                );
                setOrganization(access.organization);
                setAccessLoaded(true);
            })
            .catch((failure) =>
                setAccessError(
                    failure instanceof Error
                        ? failure.message
                        : 'Could not load the access'
                )
            );
    }, [open, dashboardId, title]);

    const onlyYou = !organization && userIds.size === 0 && groupIds.size === 0;

    const alreadyExported = (exportState?.version ?? 0) > 0;

    const submit = async () => {
        setExporting(true);
        setError(null);
        try {
            await onExport(
                name.trim(),
                grouping,
                accessLoaded
                    ? {
                          userIds: [...userIds],
                          groupIds: [...groupIds],
                          organization
                      }
                    : undefined
            );
            onOpenChange(false);
        } catch (failure) {
            setError(
                failure instanceof Error ? failure.message : 'Could not export'
            );
        } finally {
            setExporting(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl">
                <DialogHeader>
                    <DialogTitle>Export to BIG</DialogTitle>
                    <DialogDescription>
                        {alreadyExported
                            ? 'This dashboard is already in BIG. Exporting again publishes the current state as a new version of the same report.'
                            : 'The dashboard is added to BIG under the Reports tab with an Artifact tag and shared on Organization artifacts. Choose its name, its group and who can view it.'}
                    </DialogDescription>
                </DialogHeader>
                <div className="h-[min(32rem,60vh)]">
                    <Tabs
                        value={tab}
                        onValueChange={setTab}
                        className="flex flex-col h-full"
                    >
                        <PillTabsList className="mb-4 shrink-0" tabs={TABS} />
                        <TabsContent
                            value="details"
                            className="overflow-y-auto"
                        >
                            <div className="flex flex-col gap-2 min-w-0">
                                <Label htmlFor="export-app-name">Name</Label>
                                <Input
                                    id="export-app-name"
                                    value={name}
                                    maxLength={200}
                                    onChange={(event) =>
                                        setName(event.target.value)
                                    }
                                    disabled={exporting}
                                />
                                <Label className="mt-2">Group name</Label>
                                <AppGroupField
                                    value={grouping}
                                    groups={groups}
                                    onChange={setGrouping}
                                    disabled={exporting}
                                />
                            </div>
                        </TabsContent>
                        <TabsContent
                            value="access"
                            className="flex flex-col flex-1 min-h-0 data-[state=inactive]:hidden"
                        >
                            {accessError ? (
                                <p className="py-6 text-muted-foreground text-sm">
                                    You cannot give others access to this
                                    dashboard: {accessError}
                                </p>
                            ) : !accessLoaded ? (
                                <TableSkeleton columns={3} />
                            ) : (
                                <DashboardAccessEditor
                                    dashboardId={dashboardId}
                                    active={open && tab === 'access'}
                                    userIds={userIds}
                                    groupIds={groupIds}
                                    organizationAccess={organization}
                                    onUserIdsChange={setUserIds}
                                    onGroupIdsChange={setGroupIds}
                                    onOrganizationAccessChange={setOrganization}
                                    scrollList
                                />
                            )}
                        </TabsContent>
                    </Tabs>
                </div>
                {alreadyExported && (
                    <div
                        role="alert"
                        className="flex items-start gap-2 bg-amber-500/10 px-3 py-2 border border-amber-500/40 rounded-lg text-amber-700 dark:text-amber-400 text-sm"
                    >
                        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                        <p>
                            Version {exportState?.version} of this report is
                            already in BIG. Updating overwrites it with the
                            dashboard as it looks now, and the old version
                            cannot be restored.
                        </p>
                    </div>
                )}
                {(onlyYou || accessError) && (
                    <div
                        role="alert"
                        className="flex items-start gap-2 bg-amber-500/10 px-3 py-2 border border-amber-500/40 rounded-lg text-amber-700 dark:text-amber-400 text-sm"
                    >
                        <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                        <p>
                            No one else has been given access, so this dashboard
                            will only be available to you in BIG.
                        </p>
                    </div>
                )}
                {error && <p className="text-destructive text-sm">{error}</p>}
                <DialogFooter>
                    <Button
                        variant="ghost"
                        onClick={() => onOpenChange(false)}
                        disabled={exporting}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        loading={exporting}
                        disabled={!name.trim()}
                    >
                        {alreadyExported
                            ? `Update to version ${exportState!.version + 1}`
                            : 'Export to BIG'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
